import { useRef, useState } from "react";
import api, { detailFromAxiosError, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { toast } from "sonner";
import { ChevronDown, Loader2, Upload } from "lucide-react";

/**
 * Activación manual con clave/PDF — acción secundaria colapsable (flujo comercial comprador).
 */
export default function LicenseCommercialActivationCollapsible({
  systemInfo,
  onLicenseInstalled,
  testId = "license-commercial-activation-collapsible",
}) {
  const [open, setOpen] = useState(false);
  const [licToken, setLicToken] = useState("");
  const [busyLic, setBusyLic] = useState(false);
  const [busyActivacionPdf, setBusyActivacionPdf] = useState(false);
  const activacionPdfInputRef = useRef(null);

  const onImportActivacionLicenciaPdf = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Selecciona un archivo PDF");
      return;
    }
    setBusyActivacionPdf(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/system/license/import-activacion-pdf", fd);
      toast.success("Licencia activada desde el PDF");
      const { data: infoData } = await api.get("/system/info");
      onLicenseInstalled?.({ info: infoData, postInstallNotice: Boolean(data?.post_install_data_notice) });
    } catch (err) {
      toast.error(
        (await detailFromAxiosError(err)) ||
          formatApiError(err.response?.data?.detail) ||
          "No se pudo leer o validar el PDF de activación",
      );
    } finally {
      setBusyActivacionPdf(false);
    }
  };

  const installLicense = async () => {
    const t = licToken.trim();
    if (!t) {
      toast.error("Pega la clave de licencia");
      return;
    }
    setBusyLic(true);
    try {
      const { data } = await api.put("/system/license", { license_token: t });
      toast.success(data?.message || "Licencia instalada");
      const { data: infoData } = await api.get("/system/info");
      onLicenseInstalled?.({
        info: infoData,
        postInstallNotice: Boolean(data?.post_install_data_notice),
      });
      setLicToken("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "No se pudo instalar la licencia");
    } finally {
      setBusyLic(false);
    }
  };

  return (
    <Collapsible open={open} onOpenChange={setOpen} data-testid={testId}>
      <CollapsibleTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-9 w-full justify-between text-xs font-semibold text-slate-700 border border-slate-200 bg-slate-50/80 hover:bg-slate-100"
          data-testid="license-commercial-activation-toggle"
        >
          Ya tengo una clave o PDF de activación
          <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent className="pt-3 space-y-3" data-testid="license-commercial-activation-section">
        <p className="text-[10px] text-slate-600 m-0">
          Use esta sección solo cuando BNT ya le entregó la clave o el PDF de activación. No es necesario para
          solicitar una licencia nueva.
        </p>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Label className="text-xs">Pegar clave de licencia</Label>
          <div>
            <input
              ref={activacionPdfInputRef}
              type="file"
              accept="application/pdf,.pdf"
              className="hidden"
              onChange={onImportActivacionLicenciaPdf}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 text-[11px] border-emerald-600 text-emerald-800"
              disabled={busyActivacionPdf || busyLic}
              onClick={() => activacionPdfInputRef.current?.click()}
              data-testid="license-commercial-import-activation-pdf"
            >
              {busyActivacionPdf ? (
                <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
              ) : (
                <Upload className="w-3.5 h-3.5 mr-1.5" />
              )}
              Importar PDF de activación
            </Button>
          </div>
        </div>
        <textarea
          value={licToken}
          onChange={(e) => setLicToken(e.target.value)}
          rows={3}
          spellCheck={false}
          lang="en"
          placeholder="base64.firma — pega aquí el token que te enviaron"
          className="w-full rounded-sm border border-slate-300 px-3 py-2 text-xs font-mono resize-none"
          data-testid="license-commercial-paste-token"
        />
        <Button
          type="button"
          size="sm"
          disabled={busyLic}
          className="h-9 bg-[#3DA873] hover:bg-[#3DA873]/90 text-xs"
          onClick={installLicense}
          data-testid="license-commercial-install-token"
        >
          {busyLic ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : null}
          {systemInfo?.license?.present ? "Reemplazar licencia" : "Instalar licencia"}
        </Button>
      </CollapsibleContent>
    </Collapsible>
  );
}
